import { useCallback, useRef } from 'react';

/**
 * Sends keyboard focus back to whatever opened an overlay.
 *
 * Radix restores focus only for dialogs it owns through `DialogTrigger`. The
 * row and quick-view dialogs are rendered by the page and driven by an `open`
 * prop instead, so closing one leaves focus on `<body>`: a keyboard user lands
 * at the top of the document and has to tab back through the sidebar and
 * filters to reach the row they came from (WCAG 2.4.3 Focus Order).
 *
 * Call `capture()` from the handler that opens the overlay, while the trigger
 * still holds focus -- reading the active element from an effect is too late,
 * because the dialog has already claimed focus by then. Call `restore()` when
 * it closes.
 */
export function useFocusReturn(): { capture: () => void; restore: () => void } {
  const triggerRef = useRef<HTMLElement | null>(null);

  const capture = useCallback(() => {
    const active = document.activeElement;
    triggerRef.current = active instanceof HTMLElement ? active : null;
  }, []);

  const restore = useCallback(() => {
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (!trigger?.isConnected) return;

    // Radix moves focus itself while tearing the dialog down, so claim it back
    // on the next frame rather than fighting that in the same tick.
    requestAnimationFrame(() => {
      if (trigger.isConnected) trigger.focus();
    });
  }, []);

  return { capture, restore };
}
